
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Star, 
  ExternalLink,
  CheckCircle,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAllResources, fetchResourceCategories, deleteResource, approveResource } from '@/services/resourceService';
import ResourceFormDialog from './ResourceFormDialog';
import { Resource } from '@/services/resourceService';
import { useToast } from '@/hooks/use-toast';

const ResourcesTab: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  const { data: resources, isLoading } = useQuery({
    queryKey: ['admin-resources'],
    queryFn: fetchAllResources,
  });

  const { data: categories } = useQuery({
    queryKey: ['resource-categories'],
    queryFn: fetchResourceCategories,
  });

  const filteredResources = resources?.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    return matchesSearch && matchesType;
  }) || [];

  const handleAddResource = () => {
    setEditingResource(null);
    setIsFormOpen(true);
  };

  const handleEditResource = (resource: Resource) => {
    setEditingResource(resource);
    setIsFormOpen(true);
  };

  const handleDeleteResource = async (id: string) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      await deleteResource(id);
      queryClient.invalidateQueries({ queryKey: ['admin-resources'] });
      toast({
        title: "Resource deleted",
        description: "The resource has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast({
        title: "Error",
        description: "Failed to delete the resource. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleApproveResource = async (id: string) => {
    try {
      await approveResource(id);
      queryClient.invalidateQueries({ queryKey: ['admin-resources'] });
      toast({
        title: "Resource approved",
        description: "The resource has been approved and is now visible to users.",
      });
    } catch (error) {
      console.error('Error approving resource:', error);
      toast({
        title: "Error",
        description: "Failed to approve the resource. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-resources'] });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'guide': return '📚';
      case 'tool': return '🔧';
      case 'template': return '📋';
      case 'video': return '🎥';
      case 'article': return '📄';
      default: return '📄';
    }
  };

  const getStatusBadge = (resource: Resource) => {
    if (resource.is_featured) {
      return <Badge variant="default" className="text-xs">Featured</Badge>;
    }
    if (resource.is_approved) {
      return <Badge variant="outline" className="text-xs">Approved</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">Pending</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Resource Management</h2>
          <p className="text-muted-foreground">Manage guides, tools, templates, and videos</p>
        </div>
        <Button onClick={handleAddResource}>
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      <Tabs value={selectedType} onValueChange={setSelectedType}>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <TabsList>
            <TabsTrigger value="all">All Types</TabsTrigger>
            <TabsTrigger value="guide">Guides</TabsTrigger>
            <TabsTrigger value="tool">Tools</TabsTrigger>
            <TabsTrigger value="template">Templates</TabsTrigger>
            <TabsTrigger value="video">Videos</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={selectedType}>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredResources.map((resource) => (
                <Card key={resource.id} className="h-full flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{getTypeIcon(resource.type)}</span>
                        <Badge variant="outline" className="text-xs">
                          {resource.type}
                        </Badge>
                        {getStatusBadge(resource)}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditResource(resource)}
                          className="p-1 h-auto"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteResource(resource.id)}
                          className="p-1 h-auto text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <CardTitle className="text-lg leading-tight">
                      <div className="line-clamp-2">
                        {resource.title}
                      </div>
                    </CardTitle>
                    
                    {resource.provider && (
                      <p className="text-sm text-muted-foreground truncate">
                        by {resource.provider}
                      </p>
                    )}
                  </CardHeader>
                  
                  <CardContent className="pt-0 flex-1 flex flex-col">
                    <CardDescription className="flex-1 mb-4">
                      <div className="line-clamp-3">
                        {resource.description}
                      </div>
                    </CardDescription>
                    
                    {resource.rating_count > 0 && (
                      <div className="flex items-center gap-1 mb-4 text-xs text-muted-foreground">
                        <Star className="h-3 w-3 fill-current text-yellow-500" />
                        <span>{resource.rating_avg.toFixed(1)}</span>
                        <span>({resource.rating_count} reviews)</span>
                      </div>
                    )}
                    
                    <div className="mt-auto flex gap-2">
                      {!resource.is_approved && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleApproveResource(resource.id)}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => window.open(resource.url, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ResourceFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        resource={editingResource}
        categories={categories || []}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default ResourcesTab;
